/**
 * venueEntry.ts — 1904va
 * Venue detection hierarchy:
 * 1. Geolocation (within event radius)
 * 2. Access code match
 * Entry creates anonymous participant immediately.
 */
import { supabase } from './supabase'
import type { Event } from '@/types'

export interface EventWithEntry extends Event {
  geo_lat?: number
  geo_lng?: number
  geo_radius_m?: number
  access_code?: string
  wifi_ssid?: string
  prize_draw_enabled?: boolean
  prize_draw_label?: string
  sponsor_name?: string
  sponsor_color?: string
  is_active?: boolean
}

// ─── Geo helpers ──────────────────────────────────────────────────────────────

function haversineMetres(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function getCurrentPosition(): Promise<GeolocationCoordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return }
    navigator.geolocation.getCurrentPosition(
      pos => resolve(pos.coords),
      err => reject(err),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    )
  })
}

// ─── Fetch active events ──────────────────────────────────────────────────────

export async function fetchActiveEvents(): Promise<EventWithEntry[]> {
  const { data, error } = await supabase
    .from('club_events')
    .select('*')
    .eq('is_active', true)
    .order('starts_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as EventWithEntry[]
}

// ─── Detect nearby events ─────────────────────────────────────────────────────

export async function detectNearbyEvents(): Promise<{
  events: EventWithEntry[]
  coords: GeolocationCoordinates | null
  method: 'geo' | 'none'
}> {
  try {
    const coords = await getCurrentPosition()
    const all = await fetchActiveEvents()
    const nearby = all.filter(ev => {
      if (!ev.geo_lat || !ev.geo_lng) return false
      const d = haversineMetres(coords.latitude, coords.longitude, ev.geo_lat, ev.geo_lng)
      return d <= (ev.geo_radius_m ?? 300)
    })
    return { events: nearby, coords, method: 'geo' }
  } catch {
    return { events: [], coords: null, method: 'none' }
  }
}

// ─── Match event by access code ───────────────────────────────────────────────

export async function matchEventByCode(code: string): Promise<EventWithEntry | null> {
  const clean = code.trim().toUpperCase()
  const { data } = await supabase
    .from('club_events')
    .select('*')
    .eq('access_code', clean)
    .eq('is_active', true)
    .single()
  return data as EventWithEntry | null
}

// ─── Generate a silent device session (no anonymous auth needed) ─────────────

function deviceFingerprint(): string {
  // Stable per device/browser — stored in localStorage so same device = same ghost identity
  const key = 'room_device_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
    localStorage.setItem(key, id)
  }
  return id
}

// ─── Create guest participant & join event (no Supabase auth required) ────────

export async function joinEventAnonymously(
  eventId: string,
  role: string,
  entryMethod: 'geo' | 'code' | 'qr' | 'manual'
) {
  const deviceId = deviceFingerprint()

  // Check if this device already has a participant (returning guest)
  const { data: existing } = await supabase
    .from('participants')
    .select('*')
    .eq('device_id', deviceId)
    .maybeSingle()

  if (existing) return existing

  // Create guest participant row — no auth user needed
  // founder_access: true during evaluation period — recorded permanently
  const { data: participant, error: pError } = await supabase
    .from('participants')
    .insert({
      name:            'Guest',
      role,
      device_id:       deviceId,
      is_anonymous:    true,
      entry_method:    entryMethod,
      active_event_id: eventId,
      founder_access:  true,
      founder_since:   new Date().toISOString(),
    })
    .select()
    .single()

  if (pError) throw pError

  // Store participant_id locally for this session
  localStorage.setItem('room_participant_id', participant.id)

  return participant
}

// ─── Capture contact (soft prompt) ───────────────────────────────────────────

export async function captureContact(participantId: string, contact: string, name?: string) {
  const update: Record<string, unknown> = { contact_captured: true }
  if (contact.includes('@')) {
    // email — update auth user
    await supabase.auth.updateUser({ email: contact })
  } else {
    update.phone = contact
  }
  if (name) update.name = name

  const { error } = await supabase
    .from('participants')
    .update(update)
    .eq('id', participantId)

  if (error) throw error
}

// ─── Enter prize draw ─────────────────────────────────────────────────────────

export async function enterPrizeDraw(eventId: string, participantId: string, contact: string) {
  await captureContact(participantId, contact)
  const { error } = await supabase
    .from('prize_draw_entries')
    .upsert({ event_id: eventId, participant_id: participantId, contact }, { onConflict: 'event_id,participant_id' })
  if (error) throw error
}
